/**
 * The kill-cam CAMERA RIG — turns the director's beats into a cinematic camera path (position + look-at + fov +
 * vignette) for every frame, with eased motion and a soft crossfade between shots (never a hard cut-jump).
 *
 * The signature is the BULLET_TIME cannonball beat: a dedicated time-ramp (`scrubTimeAt`) collapses sim-time to a
 * near-freeze while the camera WHIP-ORBITS the frozen ball 180–360° with ease-in/out acceleration, holds the
 * beat, then SNAPS time back for the burst. That time-ramp is deliberately separate from the ordinary slow-mo
 * pinch the linear beats use.
 *
 * PURE of THREE / DOM (plain-number math + a reused output object) — the ramp/easing/orbit are unit-tested in
 * directorcamera.test.ts, and the player just copies the numbers onto renderer.camera.
 */
import { BULLET_TIME, REPLAY } from '@piratas/protocol';
import type { Beat, Cut } from './shotdirector.js';
import { solveTwoShip, DEFAULT_SOLVE, type SolveOpts } from './framesolver.js';

export interface CamState { px: number; py: number; pz: number; lx: number; ly: number; lz: number; fov: number; vignette: number; }
/** sim (x, y) [+ optional heading] → position lookup for a ship/ball id, or null when absent this frame. */
export type Lookup = (id: number) => { x: number; y: number; heading?: number } | null;
export type SeaY = (simX: number, simY: number) => number;

export function clamp01(t: number): number { return t < 0 ? 0 : t > 1 ? 1 : t; }
export function easeInOutCubic(t: number): number { t = clamp01(t); return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
export function easeOutCubic(t: number): number { t = clamp01(t); return 1 - Math.pow(1 - t, 3); }
export function easeInCubic(t: number): number { t = clamp01(t); return t * t * t; }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

const DEG = Math.PI / 180;
/** Clamp the orbit sweep into the sane 180–360° band (owner knob is authored freehand). */
function clampOrbitDeg(deg: number): number { return deg < 180 ? 180 : deg > 360 ? 360 : deg; }

/** The five real-time phases of the bullet-time beat, as cumulative offsets (seconds into the beat). */
function bulletPhases(): { approachEnd: number; rampEnd: number; orbitEnd: number; holdEnd: number; releaseEnd: number } {
  const approachEnd = BULLET_TIME.APPROACH_S;
  const rampEnd = approachEnd + BULLET_TIME.RAMP_IN_S;
  const orbitEnd = rampEnd + BULLET_TIME.ORBIT_S;
  const holdEnd = orbitEnd + BULLET_TIME.HOLD_S;
  const releaseEnd = holdEnd + BULLET_TIME.RELEASE_S;
  return { approachEnd, rampEnd, orbitEnd, holdEnd, releaseEnd };
}

/**
 * Map real-seconds-into-a-beat → sim time to display (the time-ramp). Linear beats are a straight lerp of their
 * sim span; the bullet beat rides the ball in (decelerating), FREEZES through the orbit + hold, then snaps to the
 * impact. Continuous with neighbouring beats (approachStart == prev scrubEnd, scrubImpact == next scrubStart).
 */
export function scrubTimeAt(beat: Beat, localReal: number): number {
  if (beat.warp !== 'bullet') {
    const p = beat.realDur > 0 ? clamp01(localReal / beat.realDur) : 0;
    return lerp(beat.scrubStart, beat.scrubEnd, p);
  }
  const ph = bulletPhases();
  if (localReal <= ph.rampEnd) {
    // approach + ramp-in: ease sim-time from approachStart → freeze, decelerating as the freeze bites
    const p = ph.rampEnd > 0 ? clamp01(localReal / ph.rampEnd) : 1;
    return lerp(beat.scrubStart, beat.scrubFreeze, easeOutCubic(p));
  }
  if (localReal <= ph.holdEnd) return beat.scrubFreeze; // orbit + hold: FROZEN
  // release: accelerate sim-time freeze → impact (the burst)
  const p = clamp01((localReal - ph.holdEnd) / Math.max(1e-4, ph.releaseEnd - ph.holdEnd));
  return lerp(beat.scrubFreeze, beat.scrubImpact, easeInCubic(p));
}

/** Instantaneous sim-time scale (d(sim)/d(real)) at a point in a bullet beat — for verification/tests. ~FREEZE
 *  during the orbit. */
export function bulletTimeScaleAt(beat: Beat, localReal: number, eps = 0.008): number {
  const a = scrubTimeAt(beat, localReal - eps), b = scrubTimeAt(beat, localReal + eps);
  return (b - a) / (2 * eps);
}

export class DirectorCamera {
  private out: CamState = { px: 0, py: 0, pz: 0, lx: 0, ly: 0, lz: 0, fov: 55, vignette: 0 };
  baseFov = 55;

  /** Active beat index for real-time R (seconds into the cut). */
  static beatAt(cut: Cut, R: number): number {
    for (let i = cut.beats.length - 1; i >= 0; i--) if (R >= cut.beats[i]!.realStart) return i;
    return 0;
  }

  /** Full camera for real-time R. `lookup` resolves ship/ball sim positions at the CURRENT sampled sim time,
   *  `seaY` gives ocean height. Crossfades the previous beat's framing in over REPLAY.CROSSFADE_S. */
  sample(cut: Cut, R: number, lookup: Lookup, seaY: SeaY, crossfadeS: number): CamState {
    const i = DirectorCamera.beatAt(cut, R);
    const beat = cut.beats[i]!;
    const local = R - beat.realStart;
    this.cameraForBeat(beat, local, lookup, seaY, this.out);
    // crossfade with the previous beat's camera at the very start of a beat (positional blend, no second sample)
    if (i > 0 && local < crossfadeS) {
      const prev = cut.beats[i - 1]!;
      const pc = TMP;
      this.cameraForBeat(prev, prev.realDur, lookup, seaY, pc);
      const k = easeInOutCubic(local / crossfadeS);
      this.out.px = lerp(pc.px, this.out.px, k); this.out.py = lerp(pc.py, this.out.py, k); this.out.pz = lerp(pc.pz, this.out.pz, k);
      this.out.lx = lerp(pc.lx, this.out.lx, k); this.out.ly = lerp(pc.ly, this.out.ly, k); this.out.lz = lerp(pc.lz, this.out.lz, k);
      this.out.fov = lerp(pc.fov, this.out.fov, k); this.out.vignette = lerp(pc.vignette, this.out.vignette, k);
    }
    this.finalize(this.out, seaY, beat.focusId, beat.targetId, lookup); // guard the blended result too
    return this.out;
  }

  /** ROBUSTNESS GUARD — no camera ever leaves this method with a NaN, a hull-piercing dip, a below-water dip, or a
   *  degenerate look (which would gimbal-flip THREE's lookAt). Applied to EVERY beat's raw output AND to the
   *  crossfade blend, so no camera transform the player ever consumes can be broken, whatever the combat geometry. */
  private finalize(o: CamState, seaY: SeaY, focusId: number, targetId: number, lookup: Lookup): void {
    if (!Number.isFinite(o.px)) o.px = 0;
    if (!Number.isFinite(o.pz)) o.pz = 0;
    if (!Number.isFinite(o.py)) o.py = seaY(o.px, o.pz) + 20;
    if (!Number.isFinite(o.lx)) o.lx = o.px + 1;
    if (!Number.isFinite(o.lz)) o.lz = o.pz;
    if (!Number.isFinite(o.ly)) o.ly = seaY(o.lx, o.lz);
    if (!Number.isFinite(o.fov)) o.fov = this.baseFov;
    o.vignette = Number.isFinite(o.vignette) ? clamp01(o.vignette) : 0;
    this.clearHulls(o, focusId, targetId, lookup, seaY); // lift over any masts the camera would otherwise pierce
    // camera never below the anti-clip / anti-flip floor above the sea surface
    const floor = seaY(o.px, o.pz) + REPLAY.CAM_MIN_HEIGHT;
    if (!(o.py >= floor)) o.py = floor;
    // CAP THE LOOK PITCH — a near-vertical look flips THREE.lookAt against the world-up vector (gimbal-flip). Push
    // the aim point outward along its horizontal bearing until |vertical/length| ≤ LOOK_MAX (pitch < ~74.6°), well
    // under the 0.985 flip threshold. Applied to EVERY beat + the crossfade blend, so no frame can ever flip. A
    // steady overhead (~65°) is under the cap, so its framing is untouched; only pathological blends get corrected.
    const LOOK_MAX = 0.965;
    let dx = o.lx - o.px, dz = o.lz - o.pz;
    const dy = o.ly - o.py;
    let horiz = Math.hypot(dx, dz);
    const minHoriz = Math.abs(dy) * Math.sqrt(1 / (LOOK_MAX * LOOK_MAX) - 1);
    if (horiz < minHoriz) {
      if (horiz < 1e-4) { dx = 1; dz = 0; horiz = 1; } // degenerate → arbitrary stable bearing
      const s = minHoriz / horiz;
      o.lx = o.px + dx * s; o.lz = o.pz + dz * s;
    }
  }

  /** Lift the camera above a framed ship's masts when its path would pierce that hull's footprint. Eased over
   *  HULL_GRAZE_BAND so the rise is gentle (never a vertical snap), full clearance by the time it reaches the hull. */
  private clearHulls(o: CamState, focusId: number, targetId: number, lookup: Lookup, seaY: SeaY): void {
    const outer = BULLET_TIME.HULL_GRAZE + BULLET_TIME.HULL_GRAZE_BAND;
    const consider = (id: number): void => {
      const s = lookup(id); if (!s) return;
      const horiz = Math.hypot(o.px - s.x, o.pz - s.y);
      if (horiz >= outer) return;
      const need = seaY(s.x, s.y) + BULLET_TIME.HULL_MAST_H;
      if (o.py >= need) return;
      const k = clamp01((outer - horiz) / BULLET_TIME.HULL_GRAZE_BAND); // 0 at outer rim, 1 at/under HULL_GRAZE
      o.py = Math.max(o.py, lerp(o.py, need, k));
    };
    consider(focusId);
    if (targetId !== focusId) consider(targetId);
  }

  /** Compose one beat's camera into `o`. */
  private cameraForBeat(beat: Beat, local: number, lookup: Lookup, seaY: SeaY, o: CamState): void {
    const p = beat.realDur > 0 ? clamp01(local / beat.realDur) : 0;
    o.fov = this.baseFov; o.vignette = 0;
    const focus = lookup(beat.focusId) ?? lookup(beat.targetId) ?? { x: beat.aimX, y: beat.aimY };
    const target = lookup(beat.targetId) ?? focus;
    const cx = (focus.x + target.x) * 0.5, cy = (focus.y + target.y) * 0.5; // action centroid (sim)
    const sepAng = Math.atan2(target.y - focus.y, target.x - focus.x);

    switch (beat.kind) {
      case 'establish': {
        // high wide opener, slow push-in toward the action
        const dist = lerp(70, 46, easeOutCubic(p));
        const h = lerp(46, 30, easeOutCubic(p));
        const ang = sepAng + Math.PI * 0.5 + beat.orbitDir * 0.25; // an off-axis 3/4 view
        o.px = cx + Math.cos(ang) * dist; o.pz = cy + Math.sin(ang) * dist; o.py = seaY(o.px, o.pz) + h;
        this.lookSim(o, cx, cy, seaY, 4);
        break;
      }
      case 'chase': {
        const f = lookup(beat.focusId) ?? focus;
        const fwd = this.headingOf(beat.focusId, lookup, sepAng);
        const dist = lerp(30, 24, easeInOutCubic(p));
        o.px = f.x - Math.cos(fwd) * dist + Math.sin(fwd) * beat.orbitDir * 6;
        o.pz = f.y - Math.sin(fwd) * dist - Math.cos(fwd) * beat.orbitDir * 6;
        o.py = seaY(o.px, o.pz) + lerp(7, 5.5, p); // low, hugging the water
        this.lookSim(o, f.x + Math.cos(fwd) * 10, f.y + Math.sin(fwd) * 10, seaY, 3);
        break;
      }
      case 'orbit': {
        const dist = lerp(42, 34, easeInOutCubic(p));      // subtle dolly-in
        const ang = sepAng + Math.PI * 0.35 + beat.orbitDir * (0.5 + easeInOutCubic(p) * 1.4);
        o.px = cx + Math.cos(ang) * dist; o.pz = cy + Math.sin(ang) * dist; o.py = seaY(o.px, o.pz) + lerp(20, 15, p);
        o.fov = this.baseFov - 2 * easeInOutCubic(p);      // gentle dolly-zoom
        this.lookSim(o, cx, cy, seaY, 4);
        break;
      }
      case 'broadside': {
        const f = lookup(beat.focusId) ?? focus;
        const head = this.headingOf(beat.focusId, lookup, sepAng);
        const side = beat.side === 1 ? 1 : -1; // view from the firing side so the muzzle smoke faces camera
        const perp = head + side * Math.PI / 2;
        const dist = lerp(30, 22, easeOutCubic(p));
        o.px = f.x + Math.cos(perp) * dist; o.pz = f.y + Math.sin(perp) * dist; o.py = seaY(o.px, o.pz) + lerp(9, 6, p);
        this.lookSim(o, f.x + Math.cos(head) * 6, f.y + Math.sin(head) * 6, seaY, 3.5);
        break;
      }
      case 'ram': {
        const ang = sepAng + Math.PI / 2 + beat.orbitDir * (0.2 + p * 0.5); // side-on at the collision, slow drift
        const dist = lerp(26, 20, easeOutCubic(p));
        o.px = beat.aimX + Math.cos(ang) * dist; o.pz = beat.aimY + Math.sin(ang) * dist; o.py = seaY(o.px, o.pz) + lerp(8, 5, p);
        o.vignette = 0.25 * easeInOutCubic(p);
        this.lookSim(o, beat.aimX, beat.aimY, seaY, 3);
        break;
      }
      case 'impact': {
        // frame the sinking hull with the hero, a slow high 3/4 push-in
        const dist = lerp(38, 28, easeOutCubic(p));
        const ang = sepAng - Math.PI * 0.4 - beat.orbitDir * 0.2;
        o.px = cx + Math.cos(ang) * dist; o.pz = cy + Math.sin(ang) * dist; o.py = seaY(o.px, o.pz) + lerp(16, 11, p);
        o.vignette = 0.2 * easeOutCubic(p);
        this.lookSim(o, target.x, target.y, seaY, 3);
        break;
      }
      case 'muzzle': {
        // BEAT 1 — side-on at the KILLER as the fatal broadside fires: muzzle smoke faces camera, a slow drift-in
        const f = lookup(beat.focusId) ?? focus;
        const head = this.headingOf(beat.focusId, lookup, sepAng);
        const side = beat.side === 1 ? 1 : -1; // view from the firing side
        const perp = head + side * Math.PI / 2;
        const dist = lerp(27, 19, easeOutCubic(p));
        o.px = f.x + Math.cos(perp) * dist; o.pz = f.y + Math.sin(perp) * dist; o.py = seaY(o.px, o.pz) + lerp(8.5, 6, p);
        o.vignette = 0.12 * easeInOutCubic(p);
        this.lookSim(o, f.x + Math.cos(head) * 5, f.y + Math.sin(head) * 5, seaY, 3.5);
        break;
      }
      case 'track': {
        // BEAT 2 — FOLLOW THE KILLING BALL: trail it along its travel, framed NEAR or FAR (beat.framing), low over
        // the water so the bullet skims the sea (the protagonist). The look leads slightly onto the doomed hull.
        const ball = lookup(beat.ballId) ?? lookup(beat.focusId) ?? { x: beat.aimX, y: beat.aimY };
        const tgt = lookup(beat.targetId) ?? { x: beat.aimX, y: beat.aimY };
        let travel = this.headingOf(beat.ballId, lookup, Math.atan2(tgt.y - ball.y, tgt.x - ball.x));
        if (!Number.isFinite(travel)) travel = 0;
        const base = lerp(11, 34, clamp01(beat.framing));         // this clip's near/far distance
        const dist = lerp(base + 6, base, easeInOutCubic(p));     // a gentle dolly-in as it flies
        const sideOff = beat.orbitDir * lerp(7, 4, p);            // ride to one side so the ball reads across frame
        o.px = ball.x - Math.cos(travel) * dist + Math.sin(travel) * sideOff;
        o.pz = ball.y - Math.sin(travel) * dist - Math.cos(travel) * sideOff;
        o.py = seaY(o.px, o.pz) + lerp(6.5, 5, p);
        this.lookAtBall(o, ball.x, ball.y, tgt, seaY);
        break;
      }
      case 'aerial': {
        // THE SPINE — a CONSTRAINT-SOLVED overhead that keeps BOTH ships inside the exported 9:16 frame ("por cima,
        // pega os dois"). Re-solved each frame off the LIVE ship positions so it tracks the action; an overhead cam
        // sits tens of metres above the masts, so it is inherently clip-safe (finalize() is only a backstop here).
        // CRANE UP over the first moments so a cut from a tight beat reads as a rising reveal, not a fast whip.
        this.solveOverhead(beat, lookup, seaY, o, this.craneHeight(beat.heightFrac, p, 0.55));
        break;
      }
      case 'sink': {
        // FILM THE NAUFRÁGIO to the END — now an overhead solve that leans onto the wreck (lookBias) while keeping
        // the killer in frame when it is still on tape (targetId). The victim's transform is HELD (last-known) by
        // the player after despawn, so the overhead holds the plunge instead of snapping to the hero. A gentle
        // crane-up in its first moments turns the post-impact cut into a rising reveal (both combatants framed).
        this.solveOverhead(beat, lookup, seaY, o, this.craneHeight(beat.heightFrac, p, 0.7));
        o.vignette = 0.18 * easeInOutCubic(p);                   // keep the sink's closing mood vignette
        // NB: no FOV dolly here — closing the FOV could drop a combatant the solver opened it to hold.
        break;
      }
      case 'cannonball': this.bulletCamera(beat, local, lookup, seaY, o); break;
    }
    this.finalize(o, seaY, beat.focusId, beat.targetId, lookup); // every beat's raw output is guarded before use/blend
  }

  /** Compose a CONSTRAINT-SOLVED overhead pose (framesolver) into `o`: the two framed ships are focusId (ship A —
   *  the one the look leans onto for the sink) and targetId (ship B; null when it is the same id / absent → a
   *  single-ship overhead). The beat carries the style (overhead pitch, frame margin, look bias). */
  /** Ease the overhead pitch up from `startFrac`×target to the target over the first `startFrac` of the beat — a
   *  crane-up so a cut from a tight beat into the overhead reveals rather than whips. */
  private craneHeight(target: number, p: number, startFrac: number): number {
    const t = target > 0 ? target : DEFAULT_SOLVE.heightFrac;
    return lerp(t * startFrac, t, easeOutCubic(Math.min(1, p / 0.35)));
  }

  private solveOverhead(beat: Beat, lookup: Lookup, seaY: SeaY, o: CamState, heightFracEff?: number): void {
    // OWNER MANDATE: an overhead beat frames the TWO combatants (focus = hero/wreck, target = the other) for 100 % of
    // its frames — NO reveal/ease that would drop one early. The solver escalates (pull-back → open FOV) to fit both.
    const A = lookup(beat.focusId) ?? { x: beat.aimX, y: beat.aimY };
    const Braw = beat.targetId !== beat.focusId ? lookup(beat.targetId) : null;
    const B = Braw ? { x: Braw.x, y: Braw.y } : null;
    const opts: SolveOpts = {
      ...DEFAULT_SOLVE,
      fov: this.baseFov,
      heightFrac: heightFracEff !== undefined ? heightFracEff : (beat.heightFrac > 0 ? beat.heightFrac : DEFAULT_SOLVE.heightFrac),
      marginFrac: beat.marginFrac > 0 ? beat.marginFrac : DEFAULT_SOLVE.marginFrac,
      side: beat.orbitDir >= 0 ? 1 : -1,
      lookBias: beat.lookBias,
      yawBias: DEFAULT_SOLVE.yawBias * (0.6 + clamp01(beat.framing) * 0.8), // seed-varied 3/4 kick for clip variety
      preferredYaw: beat.preferredYaw,                                      // the director's STABLE per-beat bearing
    };
    const pose = solveTwoShip({ x: A.x, y: A.y }, B, seaY, opts);
    o.px = pose.px; o.py = pose.py; o.pz = pose.pz;
    o.lx = pose.lx; o.ly = pose.ly; o.lz = pose.lz;
    o.fov = pose.fov; o.vignette = 0;
  }

  /**
   * Pick a SAFE orbit radius + height for the bullet-time whip so the ring never enters a hull, whatever the
   * combat distance. The ring is centred on the frozen ball (≈ the impact point):
   *  • radius is floored so it always clears the TARGET hull (which sits ~at the centre), and
   *  • the camera LIFTS above the masts of any ship (target or hero) whose hull footprint the low ring would
   *    otherwise sweep through — so a close-range kill spins from a clean high angle instead of clipping.
   * All clamped into [ORBIT_RADIUS_MIN, ORBIT_RADIUS_MAX] × [ORBIT_HEIGHT, ORBIT_HEIGHT_MAX].
   */
  private safeOrbit(cx: number, cy: number, focusId: number, targetId: number, lookup: Lookup): { r: number; h: number } {
    const tgt = lookup(targetId), hero = lookup(focusId);
    const dOf = (s: { x: number; y: number } | null): number => (s ? Math.hypot(s.x - cx, s.y - cy) : NaN);
    const dt = dOf(tgt), dh = dOf(hero);
    let r: number = BULLET_TIME.ORBIT_RADIUS;
    if (Number.isFinite(dt)) r = Math.max(r, dt + BULLET_TIME.HULL_CLEAR); // never inside the target hull
    r = Math.min(Math.max(r, BULLET_TIME.ORBIT_RADIUS_MIN), BULLET_TIME.ORBIT_RADIUS_MAX);
    let h: number = BULLET_TIME.ORBIT_HEIGHT;
    for (const d of [dt, dh]) {
      if (!Number.isFinite(d)) continue;
      const cmin = Math.abs(r - d); // the camera's closest horizontal approach to that hull's centre over the ring
      if (cmin < BULLET_TIME.HULL_GRAZE) {
        const k = clamp01((BULLET_TIME.HULL_GRAZE - cmin) / BULLET_TIME.HULL_GRAZE);
        h = Math.max(h, lerp(BULLET_TIME.ORBIT_HEIGHT, BULLET_TIME.HULL_MAST_H, k)); // rise over the masts
      }
    }
    return { r, h: Math.min(h, BULLET_TIME.ORBIT_HEIGHT_MAX) };
  }

  /** The Matrix bullet-time camera: ride the ball inbound → whip-orbit the frozen impact point → ease out to the
   *  burst framing. Robust: ONE stable orbit centre (the impact/aim point) drives every phase — so the camera path
   *  is continuous end-to-end and NEVER lurches when the live ball snaps forward during the release. The radius +
   *  height are chosen by safeOrbit() each frame so the ring never enters a hull, even when the ships are colliding.
   *  The LOOK still tracks the live ball, so we always SEE the bullet ride in and burst. */
  private bulletCamera(beat: Beat, local: number, lookup: Lookup, seaY: SeaY, o: CamState): void {
    const ph = bulletPhases();
    const ball = lookup(beat.ballId);
    const tgt = lookup(beat.targetId) ?? { x: beat.aimX, y: beat.aimY };
    // ONE stable orbit centre for the whole beat: the impact/closest-approach point (never the snapping live ball)
    const cx = beat.aimX, cy = beat.aimY;
    const lookBx = ball ? ball.x : cx, lookBy = ball ? ball.y : cy; // live ball for the LOOK only
    // stable ball→target bearing (falls back to the ball heading, then the centre→target axis, if degenerate)
    let travel = Math.atan2(tgt.y - cy, tgt.x - cx);
    if ((tgt.x - cx) * (tgt.x - cx) + (tgt.y - cy) * (tgt.y - cy) < 1e-4) {
      const bh = ball && typeof ball.heading === 'number' ? ball.heading : NaN;
      travel = Number.isFinite(bh) ? bh : Math.atan2(lookBy - cy, lookBx - cx);
      if (!Number.isFinite(travel)) travel = 0;
    }
    const swept = clampOrbitDeg(BULLET_TIME.ORBIT_DEG) * DEG;
    const { r: R, h: H } = this.safeOrbit(cx, cy, beat.focusId, beat.targetId, lookup);
    o.fov = this.baseFov;
    if (local <= ph.rampEnd) {
      // APPROACH + RAMP: trail the impact point from behind, easing a slightly-wider chase down to the orbit ring
      const p = clamp01(local / ph.rampEnd);
      const dist = lerp(R + 9, R, easeInOutCubic(p));
      const back = travel + Math.PI - beat.orbitDir * 0.5 * (1 - p); // swing into the orbit's START angle (continuous at rampEnd)
      o.px = cx + Math.cos(back) * dist; o.pz = cy + Math.sin(back) * dist;
      o.py = seaY(o.px, o.pz) + lerp(Math.max(6, H - 2), H, easeInOutCubic(p));
      o.vignette = 0.15 * p;
      this.lookAtBall(o, lookBx, lookBy, tgt, seaY);
    } else if (local <= ph.holdEnd) {
      // ORBIT + HOLD: whip-orbit the frozen impact point with ease-in/out; hold the final angle through HOLD
      const orbLocal = clamp01((local - ph.rampEnd) / BULLET_TIME.ORBIT_S);
      const ang = travel + Math.PI + beat.orbitDir * easeInOutCubic(orbLocal) * swept; // begin behind, sweep round
      o.px = cx + Math.cos(ang) * R; o.pz = cy + Math.sin(ang) * R;
      o.py = seaY(o.px, o.pz) + H;
      o.fov = this.baseFov - BULLET_TIME.FOV_PUNCH_DEG * easeInOutCubic(Math.min(1, orbLocal * 1.5)); // dolly-zoom in
      o.vignette = BULLET_TIME.VIGNETTE * easeOutCubic(Math.min(1, orbLocal * 2));
      this.lookAtBall(o, lookBx, lookBy, tgt, seaY);
    } else {
      // RELEASE: from the SAME centre, ease the ring out + up to a clean impact framing as time snaps back. The
      // orbit angle is continuous with the hold, and the centre is fixed — so the burst never yanks the camera.
      const p = clamp01((local - ph.holdEnd) / Math.max(1e-4, ph.releaseEnd - ph.holdEnd));
      const endA = travel + Math.PI + beat.orbitDir * swept;
      const r = lerp(R, Math.max(R + 12, 26), easeInCubic(p));
      o.px = cx + Math.cos(endA) * r; o.pz = cy + Math.sin(endA) * r;
      o.py = seaY(o.px, o.pz) + lerp(H, Math.max(H + 4, 10), easeInCubic(p));
      o.fov = lerp(this.baseFov - BULLET_TIME.FOV_PUNCH_DEG, this.baseFov, easeOutCubic(p));
      o.vignette = BULLET_TIME.VIGNETTE * (1 - easeInCubic(p));
      this.lookAtBall(o, lookBx, lookBy, tgt, seaY); // look follows the live ball as it bursts on the hull
    }
    // hull clearance (lift over masts) is applied uniformly by finalize() to every beat + the crossfade blend.
  }

  private lookAtBall(o: CamState, bx: number, by: number, tgt: { x: number; y: number }, seaY: SeaY): void {
    // compose slightly toward the target so the doomed hull sits behind the bullet
    const lx = lerp(bx, tgt.x, 0.28), ly = lerp(by, tgt.y, 0.28);
    o.lx = lx; o.ly = seaY(lx, ly) + 2.5; o.lz = ly;
  }

  private lookSim(o: CamState, simX: number, simY: number, seaY: SeaY, lift: number): void {
    o.lx = simX; o.ly = seaY(simX, simY) + lift; o.lz = simY;
  }

  /** Heading of a ship id (the player's lookup carries it); falls back to the two-ship separation axis. */
  private headingOf(id: number, lookup: Lookup, fallback: number): number {
    const s = lookup(id);
    return s && typeof s.heading === 'number' ? s.heading : fallback;
  }
}

const TMP: CamState = { px: 0, py: 0, pz: 0, lx: 0, ly: 0, lz: 0, fov: 55, vignette: 0 };
